import { GetPipelineStageProjectResponse } from '@/api/queries/pipelines/pipelineStageProjects';
import usePreferredNumberFormat from '@/hooks/usePreferredNumberFormat';
import { useToastContext } from '@payaca/ambrosia';

import ProjectCard, {
  ProjectCardLink,
  TProps as TProjectCardProps,
} from '@payaca/components/projectCard/ProjectCard';
import { singularPlural } from '@payaca/utilities/stringUtilities';
import React, { ComponentPropsWithRef, FC } from 'react';

import {
  formatCompactProjectValue,
  formatShortDuration,
} from './projectCardFormatters';

const useProjectCardDataProps = (
  project: GetPipelineStageProjectResponse
): TProjectCardProps => {
  const { pushSuccess } = useToastContext();
  const numberFormat = usePreferredNumberFormat();
  const locale = numberFormat.resolvedOptions().locale;

  const alerts: TProjectCardProps['alerts'] = [];
  if (project.aggregations?.isAnyInvoiceOverdue) {
    alerts.push({
      iconName: 'alert-triangle.3',
      helperText: 'One or more invoices are overdue',
      colour: 'yellow',
    });
  }
  if (project.aggregations?.isAnyProposalOrInvoiceBounced) {
    alerts.push({
      iconName: 'alert-triangle.3',
      helperText: 'One or more proposals or invoices have bounced',
      colour: 'red',
    });
  }
  if (project.aggregations?.hasCustomerViewedAllSentProposalsAndInvoices) {
    alerts.push({
      iconName: 'eye.3',
      helperText: 'Customer has viewed all sent proposals and invoices',
      colour: 'gray',
    });
  }

  const incompleteTasksCount = project.aggregations?.incompleteTasksCount ?? 0;
  if (incompleteTasksCount > 0) {
    alerts.push({
      iconName: 'list.3',
      helperText: singularPlural(incompleteTasksCount, 'incomplete task'),
      colour: 'gray',
    });
  }

  return {
    reference: project.reference,
    onCopyReference: () => {
      void navigator.clipboard.writeText(`#${project.reference}`).then(() => {
        pushSuccess('Copied to clipboard');
      });
    },
    customerName: project.customer?.name,
    address: project.addresses.site[0],
    assignee: project.owner
      ? {
          firstName: project.owner.firstName,
          lastName: project.owner.lastName,
          userColour: project.owner.colour,
          emailAddress: project.owner.email,
          imgSrc: project.owner.avatarUrl,
        }
      : undefined,
    summaryInfo: [
      ...(project.totals.projectValue.value > 0
        ? [
            {
              label: 'Project value',
              value: formatCompactProjectValue(
                project.totals.projectValue,
                locale
              ),
              iconName: 'coins-stacked-01.3' as const,
            },
          ]
        : []),
      ...(project.latestActivityAt
        ? [
            {
              label: 'Last activity',
              value: formatShortDuration(
                new Date(project.latestActivityAt),
                new Date()
              ),
              iconName: 'pencil-line.3' as const,
            },
          ]
        : []),
      ...(project.pipelineStageTransitionAuditLogs.items[0]
        ?.enteredTargetStageAt
        ? [
            {
              label: 'Time in stage',
              value: formatShortDuration(
                new Date(
                  project.pipelineStageTransitionAuditLogs.items[0]
                    .enteredTargetStageAt
                ),
                new Date()
              ),
              iconName: 'clock-stopwatch.3' as const,
            },
          ]
        : []),
    ],
    tags: project.tags.map((tag) => ({
      colour: tag.colour || 'gray',
      label: tag.name,
    })),
    alerts,
  };
};

export type TProps = {
  project: GetPipelineStageProjectResponse;
};

export const ProjectCardWithData: FC<TProps & ComponentPropsWithRef<'div'>> = (
  props
) => {
  const { ref, project, ...rest } = props;

  const projectCardProps = useProjectCardDataProps(project);

  return <ProjectCard ref={ref} {...projectCardProps} {...rest} />;
};

export const ProjectCardLinkWithData: FC<
  TProps & ComponentPropsWithRef<'a'>
> = (props) => {
  const { ref, project, ...rest } = props;

  const projectCardProps = useProjectCardDataProps(project);

  return (
    <ProjectCardLink
      ref={ref}
      to={`/projects/${project.publicId}`}
      {...projectCardProps}
      {...rest}
    />
  );
};
